import * as echarts from 'echarts';
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Skeleton,
} from '@mui/material';
import { DASHBOARD_COLORS } from '../../../../constants';

interface CategoryData {
  label: string;
  value: number;
}

export interface TimeData {
  [key: string]: CategoryData[];
}

export interface IPieCartWithStats {
  label: string;
  data: TimeData | undefined;
  loading?: boolean;
  colors?: string[];
  type?: 'donut' | 'column' | 'line' | 'bar';
  hideToggles?: boolean;
  maxValue?: number;
  legendWidth?: string | number;
  barWidthOverride?: string | number;
}

export const PieCartWithStats: React.FC<IPieCartWithStats> = ({
  label,
  data,
  loading = false,
  colors = DASHBOARD_COLORS,
  type = 'donut',
  hideToggles = false,
  maxValue,
  legendWidth = '32%',
  barWidthOverride,
}) => {
  const [timeRange, setTimeRange] = useState<string>('Week');
  const chartRef = useRef<HTMLDivElement | null>(null);

  const selectedData = data?.[timeRange] ?? [];
  const totalValue = selectedData.reduce((sum, item) => sum + item.value, 0);

  // For Area charts to 'fill', they need at least 2 points.
  // If we have only 1 point, we add a leading zero point.
  const displayData =
    type === 'line' && selectedData.length === 1
      ? [{ label: '', value: 0 }, ...selectedData]
      : selectedData;

  const noDataMessages: Record<string, string> = {
    Week: 'No data for the past week.',
    Month: 'No data for the last month.',
    '6 Months': 'No insights for 6 months.',
  };

  useEffect(() => {
    if (!chartRef.current || loading || displayData.length === 0) return;

    const chart = echarts.init(chartRef.current);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => chart.resize())
        : null;

    let option: echarts.EChartsOption = {};

    const nonZeroItems = displayData.filter((item) => item.value > 0);

    const appleTooltipStyle = {
      backgroundColor: 'rgba(255, 255, 255, 0.96)',
      textStyle: {
        color: '#0f172a',
        fontSize: 12,
        fontWeight: 500,
        fontFamily: '"Quicksand", Inter, system-ui, sans-serif',
      },
      borderWidth: 0,
      padding: [8, 12],
      extraCssText:
        'backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); border-radius: 10px; box-shadow: 0 4px 20px -2px rgba(0,0,0,0.12), 0 2px 6px -1px rgba(0,0,0,0.06); border: 1px solid rgba(226, 232, 240, 0.8);',
    };

    if (type === 'donut') {
      option = {
        tooltip: {
          trigger: 'item',
          formatter:
            '{b}: <span style="font-weight:700; color:#0f172a;">{c}</span> <span style="color:#64748b;">({d}%)</span>',
          ...appleTooltipStyle,
        },
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '40%',
            style: {
              text: totalValue.toString(),
              fill: '#0f172a',
              fontSize: 26,
              fontWeight: 'bold',
              fontFamily: '"Quicksand", Inter, system-ui, sans-serif',
            },
          },
          {
            type: 'text',
            left: 'center',
            top: '58%',
            style: {
              text: 'TOTAL',
              fill: '#94a3b8',
              fontSize: 10,
              fontWeight: 'bold',
              fontFamily: '"Quicksand", Inter, system-ui, sans-serif',
            },
          },
        ],
        series: [
          {
            name: label,
            type: 'pie',
            radius: ['68%', '86%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            padAngle: nonZeroItems.length > 1 ? 3 : 0,
            itemStyle: {
              borderRadius: nonZeroItems.length > 1 ? 6 : 0,
            },
            label: { show: false },
            emphasis: {
              scale: true,
              scaleSize: 4,
              label: { show: false },
            },
            data: displayData.map((item, index) => ({
              value: item.value,
              name: item.label,
              itemStyle: {
                color: colors[index % colors.length],
              },
            })),
          },
        ],
      };
    } else if (type === 'column') {
      option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any) => {
            const item = Array.isArray(params) ? params[0] : params;
            return `<span style="display:block; font-size:11px; color:#64748b;">${item.name}</span><span style="display:block; font-weight:700; font-size:13px; color:#0f172a; margin-top:2px;">${item.value}</span>`;
          },
          ...appleTooltipStyle,
        },
        grid: {
          left: '2%',
          right: '3%',
          bottom: '2%',
          top: '12%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: displayData.map((item) => item.label),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#e2e8f0' } },
          axisLabel: {
            color: '#64748b',
            fontSize: 11,
            fontWeight: 500,
            fontFamily: '"Quicksand", Inter, system-ui, sans-serif',
            rotate: 25,
          },
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
          axisLabel: {
            color: '#94a3b8',
            fontSize: 10,
            fontFamily: '"Quicksand", Inter, system-ui, sans-serif',
          },
          max: maxValue,
        },
        series: [
          {
            data: displayData.map((item, index) => ({
              value: item.value,
              itemStyle: { color: colors[index % colors.length] },
            })),
            type: 'bar',
            barWidth: barWidthOverride || 16,
            itemStyle: { borderRadius: [6, 6, 0, 0] },
          },
        ],
      };
    } else if (type === 'bar') {
      option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any) => {
            const item = Array.isArray(params) ? params[0] : params;
            return `<span style="display:block; font-size:11px; color:#64748b;">${item.name}</span><span style="display:block; font-weight:700; font-size:13px; color:#0f172a; margin-top:2px;">${item.value}</span>`;
          },
          ...appleTooltipStyle,
        },
        grid: {
          left: '2%',
          right: '8%',
          bottom: '4%',
          top: '6%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
          axisLabel: {
            color: '#94a3b8',
            fontSize: 10,
            fontFamily: '"Quicksand", Inter, system-ui, sans-serif',
          },
          max: maxValue,
        },
        yAxis: {
          type: 'category',
          data: displayData.map((item) => item.label),
          inverse: true,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#e2e8f0' } },
          axisLabel: {
            color: '#64748b',
            fontSize: 11,
            fontWeight: 500,
            fontFamily: '"Quicksand", Inter, system-ui, sans-serif',
          },
        },
        series: [
          {
            data: displayData.map((item, index) => ({
              value: item.value,
              itemStyle: { color: colors[index % colors.length] },
            })),
            type: 'bar',
            barWidth: barWidthOverride || 12,
            itemStyle: { borderRadius: [0, 6, 6, 0] },
          },
        ],
      };
    } else if (type === 'line') {
      option = {
        tooltip: {
          trigger: 'axis',
          formatter: (params: any) => {
            const item = Array.isArray(params) ? params[0] : params;
            return `<span style="display:block; font-size:11px; color:#64748b;">${item.name}</span><span style="display:block; font-weight:700; font-size:13px; color:#0f172a; margin-top:2px;">${item.value}</span>`;
          },
          ...appleTooltipStyle,
        },
        grid: {
          left: '4%',
          right: '6%',
          bottom: '10%',
          top: '12%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: displayData.map((item) => item.label),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#e2e8f0' } },
          axisLabel: {
            color: '#64748b',
            fontSize: 11,
            fontWeight: 500,
            fontFamily: '"Quicksand", Inter, system-ui, sans-serif',
            margin: 12,
          },
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
          axisLabel: {
            color: '#94a3b8',
            fontSize: 10,
            fontFamily: '"Quicksand", Inter, system-ui, sans-serif',
          },
        },
        series: [
          {
            data: displayData.map((item) => item.value),
            type: 'line',
            smooth: 0.4,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 7,
            lineStyle: {
              width: 3.5,
              color: colors[0],
              shadowBlur: 8,
              shadowColor: 'rgba(99, 102, 241, 0.2)',
            },
            areaStyle: {
              opacity: 0.25,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: colors[0] },
                { offset: 1, color: 'rgba(255, 255, 255, 0)' },
              ]),
            },
            itemStyle: {
              color: colors[0],
              borderColor: '#ffffff',
              borderWidth: 2,
            },
          },
        ],
      };
    }

    chart.setOption(option);

    const handleResize = () => chart.resize();
    resizeObserver?.observe(chartRef.current);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
      chart.dispose();
    };
  }, [displayData, label, colors, loading, totalValue, type]);

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        borderRadius: 4,
        border: '1px solid rgba(226, 232, 240, 0.8)',
        bgcolor: '#FFFFFF',
        p: { xs: 2, sm: 2.5 },
        boxShadow:
          '0 1px 3px rgba(0,0,0,0.03), 0 6px 16px rgba(0,0,0,0.02)',
        height: { xs: 265, sm: 285, lg: 300 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          borderColor: 'rgba(203, 213, 225, 0.8)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          minWidth: 0,
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#0F172A',
            letterSpacing: '-0.01em',
          }}
        >
          {label}
        </Typography>

        {!hideToggles && (
          <Box
            sx={{
              display: 'flex',
              flexShrink: 0,
              bgcolor: 'rgba(241, 245, 249, 0.9)',
              p: 0.5,
              borderRadius: 2.5,
              border: '1px solid rgba(226, 232, 240, 0.6)',
              gap: 0.5,
            }}
          >
            {['Week', 'Month', '6 Months'].map((range) => {
              const isSelected = timeRange === range;
              return (
                <Button
                  key={range}
                  size='small'
                  onClick={() => setTimeRange(range)}
                  sx={{
                    minWidth: 'auto',
                    px: 1.25,
                    py: 0.25,
                    fontSize: '0.6875rem',
                    fontWeight: isSelected ? 600 : 500,
                    borderRadius: 2,
                    textTransform: 'none',
                    bgcolor: isSelected ? '#FFFFFF' : 'transparent',
                    color: isSelected ? '#0F172A' : '#64748B',
                    boxShadow: isSelected
                      ? '0 1px 3px rgba(0,0,0,0.08)'
                      : 'none',
                    '&:hover': {
                      bgcolor: isSelected ? '#FFFFFF' : 'rgba(0,0,0,0.04)',
                      color: '#0F172A',
                    },
                  }}
                >
                  {range === '6 Months' ? '6M' : range}
                </Button>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Body */}
      {loading ? (
        <Skeleton
          variant='rounded'
          sx={{
            height: '100%',
            borderRadius: 3,
            bgcolor: 'rgba(248, 250, 252, 0.8)',
          }}
        />
      ) : selectedData.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            minWidth: 0,
            alignItems: 'center',
            gap: 1.5,
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Chart Container */}
          <Box ref={chartRef} sx={{ minWidth: 0, flex: 1, height: '100%' }} />

          {/* Legend Container */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              borderLeft: '1px solid #F1F5F9',
              position: 'relative',
              pl: 1.5,
              pr: 0.5,
              gap: 0.75,
              overflowY: 'auto',
              maxHeight: '100%',
              width: typeof legendWidth === 'number' ? `${legendWidth}px` : legendWidth,
            }}
          >
            {selectedData.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.25,
                  '&:hover span': {
                    color: '#0F172A',
                  },
                }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={1}
                  sx={{ maxWidth: '75%', minWidth: 0 }}
                >
                  <Box
                    component='span'
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: colors[index % colors.length],
                    }}
                  />
                  <Typography
                    variant='caption'
                    noWrap
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 500,
                      color: '#475569',
                      transition: 'color 0.15s ease',
                    }}
                  >
                    {entry.label}
                  </Typography>
                </Stack>
                <Typography
                  variant='caption'
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#1E293B',
                    fontVariantNumeric: 'tabular-nums',
                    flexShrink: 0,
                  }}
                >
                  {entry.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            px: 2,
          }}
        >
          <Typography
            variant='caption'
            sx={{ fontSize: '0.75rem', color: '#94A3B8', fontWeight: 500 }}
          >
            {noDataMessages[timeRange] ?? 'No data to display.'}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default PieCartWithStats;
